import React from "react";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { Collapse } from "@mui/material";
import { Process } from "types/types";
import TileButterfly from "./ExpandedTileModules/TileButterflyModule";
import styled from "styled-components/macro";
import {
  darkModeCardBorderColor,
  darkModeTextColor,
  medGrey,
} from "constants/colors";
import { useExpandedTileFlags } from "../processTileUtils";
import { useLatestSegmentForProcess } from "./expandedTileUtils";
import { TileSkuModule } from "./ExpandedTileModules/TileSkuModule";
import { TileOperatorNameModule } from "./ExpandedTileModules/TileOperatorNameModule";
import { TILE_DIVIDER_HEIGHT } from "pages/useTileSize";
import { TileCountModule } from "./ExpandedTileModules/TileCountModule";
import { TileAheadBehindModule } from "./ExpandedTileModules/TileAheadBehindModule";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { useIsDarkMode } from "store/atoms";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { LineRolesEnum } from "constants/constants";
import { useVisibleProcesses } from "utils/customHooks/genericHooks";
import { TileTeamSizeModule } from "./ExpandedTileModules/TileTeamSizeModule";
import { TopLossesModule } from "./ExpandedTileModules/TopLossesModule";
import TileMetricPercentModule from "./ExpandedTileModules/TileMetricPercentModule";

const ExpandedTileContent = React.memo<{
  process: Process;
  isAtLeastOneProcessWithLineRole: boolean;
  showMetricPercentWhileCollapsed?: boolean;
}>(
  ({
    process,
    isAtLeastOneProcessWithLineRole,
    showMetricPercentWhileCollapsed,
  }) => {
    // config
    const { isLargeTiles, areProcessTilesExpanded: expanded } = useSettings();

    const {
      showOperatorName,
      showTeamSize,
      showTileButterfly,
      showSkuId,
      showSkuName,
      showCount,
      showAheadBehindForProcess,
      showMetricPercent: showMetricPercentState,
      showTopLosses,
    } = useExpandedTileState(process.id);

    const latestSegment = useLatestSegmentForProcess(process.id);
    const pnlCategory = latestSegment?.pnl.category;

    const isTileWithoutRoleAmongTilesWithRoles =
      isAtLeastOneProcessWithLineRole &&
      (!process.lineRole || process.lineRole === LineRolesEnum.STANDARD);
    const visibleProcesses = useVisibleProcesses();
    const manyProcessesDisableCollapseForPerformance =
      visibleProcesses.length > 12;
    const CollapseOrHide = manyProcessesDisableCollapseForPerformance
      ? Hide
      : Collapse;
    const [isDarkMode] = useIsDarkMode();
    return (
      <CollapseOrHide
        in={expanded || showMetricPercentWhileCollapsed}
        unmountOnExit={true}
      >
        <ErrorBoundary id="ExpandedTileContent">
          <ExpandedTileContentStyles
            {...{
              $isLoading: !pnlCategory,
              $isLargeTiles: isLargeTiles,
              $showAheadBehindForProcess: showAheadBehindForProcess,
              $isDarkMode: isDarkMode,
            }}
            data-server-names={[
              SERVER_NAMES.midgardDataDumpsFullRangeByProcess,
            ]}
          >
            <div className="tile-content">
              <CollapseOrHide
                in={
                  // metric percent may show even while !expanded
                  showMetricPercentState ||
                  // but collapse it if the process has no lineRole
                  // (only those with lineRole show metricPercent while collapsed)
                  (!isTileWithoutRoleAmongTilesWithRoles &&
                    showMetricPercentWhileCollapsed)
                }
              >
                <ErrorBoundary fallback="!tile-metric-percent">
                  <TileMetricPercentModule {...{ process }} />
                </ErrorBoundary>
                <div className="tile-divider" />
              </CollapseOrHide>
              <CollapseOrHide
                // must be expanded to show operator name
                in={expanded && showOperatorName}
              >
                <TileOperatorNameModule {...{ process }} />
                <div className="tile-divider" />
              </CollapseOrHide>
              <CollapseOrHide in={expanded && (showSkuId || showSkuName)}>
                <TileSkuModule {...{ process, showSkuName, showSkuId }} />
                <div className="tile-divider" />
              </CollapseOrHide>
              <CollapseOrHide in={expanded && showTeamSize}>
                <TileTeamSizeModule {...{ process }} />
                <div className="tile-divider" />
              </CollapseOrHide>
              <CollapseOrHide in={expanded && showCount}>
                <TileCountModule {...{ process }} />
                <div className="tile-divider" />
              </CollapseOrHide>
              <CollapseOrHide in={expanded && showAheadBehindForProcess}>
                <TileAheadBehindModule {...{ process }} />
                {/* AheadBehindModule comes with its own bottom border */}
              </CollapseOrHide>
            </div>
            <CollapseOrHide in={expanded && showTileButterfly}>
              <TileButterfly {...{ process }} />
            </CollapseOrHide>
            <CollapseOrHide in={expanded && showTopLosses}>
              <TopLossesModule {...{ process }} />
            </CollapseOrHide>
          </ExpandedTileContentStyles>
        </ErrorBoundary>
      </CollapseOrHide>
    );
  }
);

export default ExpandedTileContent;

const ExpandedTileContentStyles = styled.div<{
  $isDarkMode: boolean;
  $showAheadBehindForProcess: boolean;
  $isLoading: boolean;
  $isLargeTiles: boolean;
}>`
  margin: ${props =>
    props.$isLoading
      ? "-" + (props.$isLargeTiles ? 4 : 2) + "px 0px 0px"
      : "0"};
  color: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : "black")};
  .tile-content {
    border: 1px solid
      ${({ $isDarkMode }) => ($isDarkMode ? darkModeCardBorderColor : medGrey)};
    border-bottom: ${props =>
        /* aheadBehind includes bottom-border inside it */
        props.$showAheadBehindForProcess
          ? 0
          : 3 * (props.$isLargeTiles ? 2 : 1)}px
      solid
      ${({ $isDarkMode }) => ($isDarkMode ? darkModeCardBorderColor : medGrey)};
    border-radius: 0 0 1px 1px;
  }
  .tile-divider {
    height: ${TILE_DIVIDER_HEIGHT}px;
    background: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeCardBorderColor : medGrey};
  }
  .MuiCollapse-root.open:last-child .tile-divider {
    display: none;
  }
`;

/** based on the current flags & settings, what expanded tiles modules are visible?
 *
 * if we pass a process id, check ahead/behind for that process; else check ahead/behind for any process
 */
export function useExpandedTileState(processId: string | null) {
  const {
    expandedProcessTileConfig: {
      isOperatorNameVisible,
      isTileButterflyVisible,
      isTeamSizeVisible,
      isSkuIdVisible,
      isSkuNameVisible,
      isTileCountVisible,
      isTileAheadBehindVisible,
      isTileMetricPercentVisible,
      isTopLossesVisible,
    },
  } = useSettings();
  const {
    isOperatorNameAllowed,
    isTileButterflyAllowed,
    isTeamSizeAllowed,
    isSkuIdAllowed,
    isSkuNameAllowed,
    isTileCountAllowed,
    isAnyTileAheadBehindAllowed,
    isTileAheadBehindAllowedForProcess,
    isTopLossesAllowed,
  } = useExpandedTileFlags(processId);

  // show the feature if it's allowed by LD AND it's enabled by the user
  const showOperatorName = isOperatorNameVisible && isOperatorNameAllowed;
  const showTileButterfly = isTileButterflyVisible && isTileButterflyAllowed;
  const showSkuId = isSkuIdVisible && isSkuIdAllowed;
  const showSkuName = isSkuNameVisible && isSkuNameAllowed;
  const showTeamSize = isTeamSizeVisible && isTeamSizeAllowed;
  const showCount =
    isTileCountVisible &&
    isTileCountAllowed &&
    // hide count module if ahead/behind is visible (redundant info)
    !(isTileAheadBehindVisible && isTileAheadBehindAllowedForProcess);
  const showTopLosses = isTopLossesVisible && isTopLossesAllowed;

  const showMetricPercent = isTileMetricPercentVisible;

  return {
    showOperatorName,
    showTileButterfly,
    showTeamSize,
    showSkuId,
    showSkuName,
    showCount,
    showAheadBehindForProcess:
      isTileAheadBehindVisible && isTileAheadBehindAllowedForProcess,
    showAnyAheadBehind: isTileAheadBehindVisible && isAnyTileAheadBehindAllowed,
    showMetricPercent,
    showTopLosses,
  };
}
function Hide({ in: isIn, children }) {
  return isIn ? children : null;
}
